<template>
    <div class="process">
        <div class="process-left">
            <checkbox :state="checkboxState"
                      @clicked="checkboxClick"
                      :parent-loading="loading || itemLoading">
            </checkbox>
            <div class="process-title">
                {{ process.pretty_name }}
                <span v-if="isMultiPerson"
                      class="multi-person-count"
                      :class="{ 'admin-clickable': auth_user.can_reassign && (process.ticks_count || 0) > 0 }"
                      :title="tickedByTitle"
                      @click="showTickReassignModal()">
                    {{ process.ticks_count || 0 }}/{{ process.ticks_required }}
                </span>
            </div>
            <div class="stat small-stat" title="Percent of whole item">
                {{ process.percent }}%
            </div>
            <div :class="['stat', 'small-stat', 'time-stat', { 'admin-clickable': auth_user.is_admin }]"
                 :title="auth_user.is_admin ? 'Click to edit estimated time' : 'Estimated time'"
                 @click="showEstTimeModal()">
                {{ estTimeDisplay }}
            </div>
            <v-easy-dialog v-model="estTimeModal">
                <div class="v-easy-dialog-wrap">
                    <div class="v-easy-dialog-title">
                        <span>{{ itemTitle }}</span>
                        <b>{{ process.pretty_name }}</b>
                    </div>

                    <div>
                        <span class="admin-modal-label">Minutes</span>
                        <input type="number" min="0" max="10000" step="1" v-model.number="modalMinutes" />
                    </div>

                    <div class="v-easy-dialog-controls">
                        <button class="btn-qapp" :disabled="loading" @click="estTimeSubmit()">Save</button>
                    </div>
                </div>
            </v-easy-dialog>
            <div v-if="process.checked"
                 :class="['stat', 'p-stat-name', {datetimeAdminStyle: auth_user.can_reassign}]"
                 @click="showUserTimeModal()"
                 :title="'Completed: ' + updatedAt">
                {{ userName }}
            </div>
            <v-easy-dialog v-model="userTimeModal">
                <div class="v-easy-dialog-wrap">
                    <div class="v-easy-dialog-title">
                        <span>{{ itemTitle }}</span>
                        <b>{{ process.pretty_name }}</b>
                    </div>

                    <div>
                        <span class="admin-modal-label">User</span>
                        <select name="user_id" v-model="modalUserId">
                            <option v-for="user in users_select"
                                    :value="user.id"
                                    :key="user.id">
                                {{ user.name }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <span class="admin-modal-label">Completed At</span>
                        <input type="text" name="datetime" v-model="modalDateTime" :class="{modalError: modalDateTimeError}" />
                    </div>

                    <div class="v-easy-dialog-controls">
                        <button class="btn-qapp" @click="userTimeSubmit()">Save</button>
                    </div>
                </div>
            </v-easy-dialog>

            <div v-if="auth_user.is_admin"
                class="process-people-toggle"
                :class="{ active: isMultiPerson, loading: numPeopleLoading }"
                @click="toggleNumPeople()"
                :title="numPeopleTitle">
                <i class="fas fa-users"></i>
            </div>
            <div class="process-print"
                @click="showPrintModal()"
                title="Print process label">
                <i class="fas fa-print"></i>
            </div>
            <v-easy-dialog v-model="printModal">
                <div class="v-easy-dialog-wrap">
                    <div class="v-easy-dialog-title title-bar">
                        Print: Process Tracking Label (Small)
                    </div>

                    <div v-if="Object.keys(printers).length">
                        <span class="admin-modal-label">Printer</span>
                        <select name="printer_name" v-model="printModalTrackingPrinter">
                            <option v-for="printer in printersTracking"
                                    :value="printer.printer_key"
                                    :key="printer.printer_key">
                                {{ printer.name }}
                            </option>
                        </select>
                    </div>
                    <div v-else>
                        <span class="admin-modal-label">No Printers Online!</span>
                    </div>

                    <div v-if="Object.keys(printers).length && quantity > 1">
                        <span class="admin-modal-label">Quantity</span>
                            <span class="checklist-radios">
                                <span>
                                    <input type="radio" id="print-quantity-default" name="print-quantity" :value="1" v-model="printModalTrackingQuantity">
                                    <label for="print-quantity-default">1</label>
                                </span>
                                <span>
                                    <input type="radio" id="print-quantity-all" name="print-quantity" :value="quantity" v-model="printModalTrackingQuantity">
                                    <label for="print-quantity-all">{{ quantity }}</label>
                                </span>
                            </span>
                    </div>

                    <div class="v-easy-dialog-controls">
                        <button
                            class="btn-qapp"
                            :disabled="!printModalTrackingPrinter || !Object.keys(printers).length"
                            @click="printTrackingSubmit()">Print</button>
                    </div>
                </div>
            </v-easy-dialog>
        </div>
        <div class="process-right">
            <div v-if="processStatus==='in_progress'" class="stat small-stat stat-in-progress">
                In Progress
            </div>
            <div v-else-if="processStatus==='blocked'" class="stat small-stat stat-blocked">
                Blocked
            </div>
            <div v-if="process.workcentre" class="stat small-stat" title="Workcentre">
                {{ process.workcentre.name }}
            </div>
        </div>
    </div>
</template>

<script>

    import Checkbox from "../production_checklist/Checkbox";
    import api from '../../api';
    import VEasyDialog from 'v-easy-dialog'
    import {default as _} from "lodash";

    export default {

        props: {
            itemTitle: {
                type: String,
                required: true,
            },
            clientName: {
                type: String,
                required: true,
            },
            companyName: {
                type: String,
            },
            quantity: {
                type: Number,
                required: true,
            },
            itemName: {
                type: String,
                required: true,
            },
            process: {
                type: Object,
                required: true,
            },
            itemLoading: {
                type: Boolean,
                required: true,
            },
            checkedSum: {
                type: Number,
                required: true,
            },
            auth_user: {
                type: Object,
                required: true,
            },
            users_select: {
                type: Array,
                required: true
            },
            printers: {
                required: true
            },
            user_id_filter: {
                type: Number,
                default: null,
            },
        },

        data: () => ({
            loading: false,
            numPeopleLoading: false,
            userTimeModal: false,
            modalUserId: null,
            modalFromUserId: null,
            modalDateTime: null,
            modalDateTimeError: false,
            printModal: false,
            printModalTrackingPrinter: null,
            printModalTrackingQuantity: 1,
            estTimeModal: false,
            modalMinutes: 0,
        }),

        components: {
            Checkbox,
            VEasyDialog,
        },

        mounted() {},

        methods: {
            showTickReassignModal() {
                if (! this.auth_user.can_reassign || ! (this.process.ticks_count > 0))
                    return false;

                if (this.loading || this.itemLoading === true)
                    return false;

                // Admin can reassign any tick; non-admin can only reassign their own
                let tickerIds = this.process.ticked_by_user_ids || [];
                if (this.auth_user.is_admin) {
                    this.modalFromUserId = tickerIds.length ? tickerIds[0] : this.auth_user.id;
                } else {
                    if (tickerIds.indexOf(this.auth_user.id) === -1) return false;
                    this.modalFromUserId = this.auth_user.id;
                }
                this.modalUserId = this.modalFromUserId;
                this.modalDateTime = this.process.updated_at || new Date().toISOString().replace('T', ' ').substring(0, 19);
                this.modalDateTimeError = false;
                this.userTimeModal = true;
            },
            showUserTimeModal() {
                if (! this.auth_user.can_reassign)
                    return false;

                // Non-admins can only click their own completion
                if (!this.auth_user.is_admin && this.process.user_id !== this.auth_user.id)
                    return false;

                if (this.loading || this.itemLoading === true)
                    return false;

                this.modalFromUserId = null;
                this.modalUserId = this.process.user_id;
                this.modalDateTime = this.updatedAt;
                this.modalDateTimeError = this.modalDatetimeHasError;
                this.userTimeModal = true;
            },
            showPrintModal() {
                if (this.loading || this.itemLoading === true)
                    return false;

                let lastUsedPrinter = localStorage.getItem('printModalTrackingPrinter');
                if (!this.printModalTrackingPrinter)
                    this.printModalTrackingPrinter = lastUsedPrinter;

                this.printModal = true;
            },
            showEstTimeModal() {
                if (! this.auth_user.is_admin)
                    return false;

                if (this.loading || this.itemLoading === true)
                    return false;

                this.modalMinutes = this.estMinutes;
                this.estTimeModal = true;
            },
            estTimeSubmit() {
                if (this.loading || this.itemLoading === true)
                    return false;

                let minutes = parseInt(this.modalMinutes, 10);
                if (isNaN(minutes) || minutes < 0 || minutes > 10000)
                    return false;

                let process = this.$actions.getProcess(this.process.item_id, this.process.id);
                this.loading = true;

                api.updateProcessEstSeconds(process.id, minutes)
                    .then(data => {
                        this.loading = false;
                        if (! data) return;
                        process.est_seconds = data.est_seconds;
                        process.percent = data.percent;
                        this.estTimeModal = false;
                    });
            },
            getProcessStatus() {
                return this.$actions.getProcessStatus(this.process.item_id, this.process.id);
            },
            checkboxClick() {
                if (this.loading || this.itemLoading === true || this.numPeopleLoading)
                    return false;

                let process = this.$actions.getProcess(this.process.item_id, this.process.id);

                this.loading = true;

                // For multi-person: toggle based on whether the acting user has ticked
                // If a user filter is active, tick on behalf of that user
                let newChecked;
                let tickUserId = null;
                if (this.isMultiPerson) {
                    let actingUserId = this.user_id_filter || this.auth_user.id;
                    let actingUserTicked = (process.ticked_by_user_ids || []).indexOf(actingUserId) !== -1;
                    newChecked = actingUserTicked ? 0 : 1;
                    tickUserId = this.user_id_filter;
                } else {
                    newChecked = process.checked === 1 ? 0 : 1;
                    process.checked = newChecked;
                }

                api.updateChecklist('process', process.id, newChecked, null, tickUserId)
                    .then(data => {
                        this.loading = false;
                        process.checked = data.checked;
                        process.updated_at = data.updated_at;
                        process.user = data.user;
                        process.user_id = data.user ? data.user.id : null;
                        process.ticks_count = data.ticks_count;
                        process.ticks_required = data.ticks_required;
                        process.ticked_by = data.ticked_by || [];
                        process.ticked_by_user_ids = data.ticked_by_user_ids || [];
                        process.num_people_required = data.num_people_required;
                    });
            },
            userTimeSubmit() {
                if (this.loading || this.itemLoading === true)
                    return false;

                if (this.modalDatetimeHasError) {
                    this.modalDateTimeError = true;
                    return false;
                }
                this.modalDateTimeError = false;

                let process = this.$actions.getProcess(this.process.item_id, this.process.id);
                this.loading = true;

                api.updateProcessUserTime(process.id, this.modalUserId, this.modalDateTime, this.modalFromUserId)
                    .then(data => {
                        this.loading = false;
                        process.checked = data.checked;
                        process.updated_at = data.updated_at;
                        process.user = data.user;
                        process.user_id = data.user ? data.user.id : null;
                        process.ticks_count = data.ticks_count;
                        process.ticks_required = data.ticks_required;
                        process.ticked_by = data.ticked_by || [];
                        process.ticked_by_user_ids = data.ticked_by_user_ids || [];
                        process.num_people_required = data.num_people_required;
                        this.modalUserId = null;
                        this.modalDateTime = null;
                        this.userTimeModal = false;
                    });
            },
            toggleNumPeople() {
                if (this.numPeopleLoading || this.loading || this.itemLoading)
                    return false;

                let process = this.$actions.getProcess(this.process.item_id, this.process.id);
                let current = process.num_people_required || 1;
                let newNum = current >= 2 ? 1 : 2;

                this.numPeopleLoading = true;

                api.updateNumPeopleRequired(process.id, newNum)
                    .then(data => {
                        this.numPeopleLoading = false;
                        process.num_people_required = data.num_people_required;
                        process.checked = data.checked;
                        process.updated_at = data.updated_at;
                        process.user = data.user;
                        process.user_id = data.user ? data.user.id : null;
                        process.ticks_count = data.ticks_count;
                        process.ticks_required = data.ticks_required;
                        process.ticked_by = data.ticked_by || [];
                        process.ticked_by_user_ids = data.ticked_by_user_ids || [];
                    });
            },
            printTrackingSubmit() {
                if (this.loading || this.itemLoading === true || !this.printModalTrackingPrinter)
                    return false;

                localStorage.setItem('printModalTrackingPrinter', this.printModalTrackingPrinter);

                this.loading = true;

                api.printLabel(
                    'tracking',
                    this.process.id,
                    this.auth_user.id,
                    this.printModalTrackingPrinter,
                    this.printModalTrackingQuantity
                )
                    .then(data => {
                        this.loading = false;
                        this.printModal = false;
                        this.printModalTrackingQuantity = 1;
                    });
            },
        },

        computed: {
            isMultiPerson() {
                return this.process.ticks_required > 1 || this.process.num_people_required > 1;
            },
            checkboxState() {
                if (!this.isMultiPerson) {
                    return this.process.checked;
                }
                let count = this.process.ticks_count || 0;
                let required = this.process.ticks_required || this.process.num_people_required || 1;
                if (count >= required) return 'checked';
                if (count > 0) return 'partial';
                return 'unchecked';
            },
            tickedByTitle() {
                let names = this.process.ticked_by || [];
                if (!names.length) return 'No one has ticked yet';
                return 'Ticked by: ' + names.join(', ');
            },
            numPeopleTitle() {
                let current = this.process.num_people_required || 1;
                return current >= 2
                    ? 'Click to set single-person (currently ' + current + '-person)'
                    : 'Click to set 2-person required';
            },
            printersTracking() {
                return _.filter(this.printers, ['modelName', 'DYMO LabelWriter 450 Twin Turbo']);
            },
            modalDatetimeHasError() {
                return ! this.modalDateTime.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2} [0-9]{2}:[0-9]{2}:[0-9]{2}$/);
            },
            estMinutes() {
                return Math.round((this.process.est_seconds || 0) / 60);
            },
            estTimeDisplay() {
                let m = this.estMinutes;
                if (m < 60) return m + 'm';
                let h = Math.floor(m / 60);
                let mm = m % 60;
                return mm === 0 ? h + 'h' : h + 'h ' + mm + 'm';
            },
            computedProcess() {
                return this.$actions.getProcess(this.process.item_id, this.process.id);
            },
            processStatus() {
                // use checkedSum here so we get reactive updates when something's checked/unchecked.
                let fake = this.checkedSum;
                return this.$actions.getProcessStatus(this.process.item_id, this.process.id);
            },
            user() {
                let process = this.computedProcess;
                return process.checked && process.user ? process.user : this.auth_user;
            },
            userId() {
                let process = this.computedProcess;
                return process.checked && process.user ? process.user.id : this.auth_user.id;
            },
            userName() {
                let process = this.computedProcess;
                return process.checked && process.user ? process.user.name : this.auth_user.name;
            },
            updatedAt() {
                let process = this.computedProcess;
                return process.updated_at || new Date().toISOString().replace('T', ' ').substring(0, 19);
            },
        },

    }
</script>

<style>
    .checklist-radios {
        display: flex;
    }
    .checklist-radios span {
        text-align: center;
        font-weight: bold;
        border: 1px solid #aeaeae;
        border-radius: 4px;
        background: #f9f8f2;
        margin-right: 20px;
        cursor: pointer;
        padding: 4px 18px 0 18px;
        display: block;
    }
    .checklist-radios label {
        font-weight: bold;
    }
    .v-easy-dialog-wrap select {
        cursor: pointer;
    }
    .process {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .process-left {
        display: flex;
        align-items: center;
        margin-left: 22px;
    }
    .process-right {
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }
    .process-title {
        padding: 0 20px 0 25px;
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .multi-person-count {
        font-size: 11px;
        font-weight: bold;
        background: #f0f0f0;
        border: 1px solid #ccc;
        border-radius: 10px;
        padding: 1px 7px;
        cursor: help;
    }
    .multi-person-count.admin-clickable {
        cursor: pointer;
    }
    .multi-person-count.admin-clickable:hover {
        background: #fffad1;
        border-color: #c2ba7b;
    }
    .time-stat {
        margin-left: -10px;
    }
    .time-stat.admin-clickable {
        cursor: pointer;
    }
    .time-stat.admin-clickable:hover {
        background-color: #5a8a3a;
    }
    .process-people-toggle {
        margin-left: 16px;
        color: #bbb;
        cursor: pointer;
        font-size: 13px;
    }
    .process-people-toggle:hover {
        color: #0d4988;
    }
    .process-people-toggle.active {
        color: #0d4988;
    }
    .process-people-toggle.loading {
        opacity: 0.5;
        pointer-events: none;
    }
    .process-print {
        margin-left: 22px;
        color: #777;
        cursor: pointer;
    }
    .process-print:hover {
        color: #000000;
    }
    .stat-in-progress {
        background-color: rgb(255, 179, 0);
    }
    .stat-blocked {
        background-color: rgb(255, 29, 29);
    }
    .p-stat-name {
        font-size: 11px !important;
        font-weight: normal !important;
        background-color: #fff !important;
        color: #444 !important;
        padding: 4px 7px !important;
        border: 1px solid #c2c2c2 !important;
    }
    .p-stat-name:hover {
        background-color: #fffad1 !important;
        border-color: #c2ba7b !important;
    }

    .v-easy-dialog--backdrop {
        background-color: #00000080;
    }
    .v-easy-dialog--backdrop-btn {
        opacity: 0;
    }
    .v-easy-dialog--content-container {
        display: flex;
        overflow-y: auto;
        position: absolute;
    }
    .v-easy-dialog-wrap {
        display: flex;
        flex-direction: column;
        border-radius: 10px;
        background-color: white;
        box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000);
    }
    .v-easy-dialog-wrap div {
        padding: 1rem;
        width: 100%;
        border-bottom: 1px solid #cbcbcb;
        display: flex;
    }
    .v-easy-dialog-wrap div:last-child {
        border: none;
    }
    .v-easy-dialog-title {
        display: flex;
        flex-direction: column;
    }
    .v-easy-dialog-title.title-bar {
        background: #0d4988;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        color: white;
        text-shadow: 0 0 0;
        font-size: 18px;
    }
    .v-easy-dialog-title .icon-line {
        margin: 6px;
    }
    .v-easy-dialog-title .icon-line .fas {
        margin-right: 10px;
        color: #434548;
    }
    .v-easy-dialog-controls {
        display: flex;
        justify-content: flex-end;
        border-top-width: calc(1px * calc(1 - 0));
        border-bottom-width: calc(1px * 0);
    }
    .admin-modal-label {
        display: block;
        width: 150px;
        align-self: center;
    }
    .modalError {
        border: 2px solid red;
    }
    .datetimeAdminStyle {
        cursor: pointer;
    }
    .datetimeAdminStyle:hover {
        background-color: #ffffd1;
    }
</style>
